import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mail } from "lucide-react";
import { mailingApi, praxisPoolApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";

export function ToMailingDialog({ open, onOpenChange, specialtyKey }) {
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [onlyDoctolib, setOnlyDoctolib] = useState(true);
  const [onlyNew, setOnlyNew] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOnlyDoctolib(true);
    setOnlyNew(false);
    mailingApi.getGroups()
      .then((r) => setGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Rundmail-Gruppen konnten nicht geladen werden"));
  }, [open, toast]);

  const submit = async () => {
    if (!groupId) { toast.error("Bitte eine Gruppe wählen"); return; }
    setBusy(true);
    try {
      const { data } = await praxisPoolApi.toMailing({
        specialty_key: specialtyKey, group_id: groupId,
        only_doctolib: onlyDoctolib, only_new: onlyNew,
      });
      toast.success(`${data.added} zu „${data.group_name}" hinzugefügt · ${data.skipped} übersprungen`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hinzufügen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="to-mailing-dialog">
        <DialogHeader>
          <DialogTitle>Zu Rundmail hinzufügen</DialogTitle>
          <DialogDescription>
            Fügt alle Praxen mit E-Mail-Adresse (aus dieser Fachrichtung) dedupliziert zu einer
            Rundmail-Gruppe hinzu.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Rundmail-Gruppe</label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger data-testid="to-mailing-group-select"><SelectValue placeholder="Gruppe wählen…" /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name} ({(g.contacts || []).length})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={onlyDoctolib} onCheckedChange={(c) => setOnlyDoctolib(!!c)} data-testid="to-mailing-only-doctolib" />
            Nur Praxen mit Doctolib-Terminkalender
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={onlyNew} onCheckedChange={(c) => setOnlyNew(!!c)} data-testid="to-mailing-only-new" />
            Nur neue Praxen
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !groupId} data-testid="to-mailing-confirm-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Hinzufügen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
